import type { SignalKind } from './otlp';

/** Navegação e HTTP em 10% das sessões; erro, web vital e agente sempre (U30). */
export const SESSION_SAMPLE_RATE = 0.1;
const ALWAYS: ReadonlySet<SignalKind> = new Set(['error', 'web-vital', 'agent']);
const SAMPLE_KEY = 'uyzy.telemetry.sampled';

export function shouldRecord(kind: SignalKind, sessionSampled: boolean): boolean {
  return ALWAYS.has(kind) || sessionSampled;
}

/**
 * Sorteio por sessão, guardado no `sessionStorage` para o reload não trocar de balde. Sem storage,
 * sorteia de novo (aceitável: só muda o denominador).
 */
export function decideSessionSampled(
  rate = SESSION_SAMPLE_RATE,
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined = safeSessionStorage(),
  random: () => number = Math.random,
): boolean {
  const stored = storage?.getItem(SAMPLE_KEY);
  if (stored === '1') return true;
  if (stored === '0') return false;
  const sampled = random() < rate;
  storage?.setItem(SAMPLE_KEY, sampled ? '1' : '0');
  return sampled;
}

function safeSessionStorage(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isIdentifierSegment(segment: string): boolean {
  if (segment === '') return false;
  if (GUID.test(segment)) return true;
  if (/^\d+$/.test(segment)) return true;
  if (/[@%.\s]/.test(segment)) return true;
  if ((segment.match(/\d/g) ?? []).length >= 3) return true;
  return segment.length > 32;
}

/**
 * Caminho da API sem id, documento, e-mail ou nome de arquivo: `/NaturalPerson/{id}/Uploads`.
 * Query string nunca entra (filtros carregam CPF, nome, e-mail).
 */
export function templatizePath(url: string): string {
  const path = url.replace(/^[a-z]+:\/\/[^/]+/i, '').split(/[?#]/)[0] ?? '';
  return path
    .split('/')
    .map((segment) => (isIdentifierSegment(segment) ? '{id}' : segment))
    .join('/');
}

/** Mensagem de erro sem sequência longa de dígitos (CPF, CNPJ, conta, cartão) e sem e-mail. */
export function scrubMessage(message: string, max = 500): string {
  return message
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '<email>')
    .replace(/\d[\d.\-/ ]{4,}\d/g, '<num>')
    .slice(0, max);
}

/** Id pseudônimo estável do usuário: SHA-256 do `sub` com prefixo fixo, 16 hex. Nunca o e-mail. */
export async function pseudonymize(sub: string): Promise<string> {
  const data = new TextEncoder().encode(`uyzy:${sub}`);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest).slice(0, 8), (b) => b.toString(16).padStart(2, '0')).join('');
}
